/**
 * VersionGuard — Detects when the backend has been redeployed with a new
 * build and offers the user a NON-DESTRUCTIVE toast to reload when they
 * choose.
 *
 * IMPORTANT: This component NEVER force-reloads the page. Previous versions
 * used `window.location.replace()` which wiped unsaved forms and caused
 * severe data loss in production (multiple pod replicas returning different
 * versions made the app reload constantly).
 *
 * Strategy:
 *  - On mount, fetch the backend's version from /api/version and remember it
 *    as the "initial" version for this session.
 *  - Every 2 minutes, poll again. If the new version differs from the
 *    initial one AND we see the SAME new version twice in a row (debounce
 *    against pod-replica flapping), show a toast with a manual "Recargar"
 *    action. The toast is only shown once per session.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

const POLL_MS = 120_000; // 2 minutes
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function VersionGuard() {
  const initialVersion = useRef(null);
  const pendingVersion = useRef(null);
  const toastShown = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const fetchVersion = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/version`, {
          credentials: "omit",
          cache: "no-store",
        });
        if (!res.ok) return null;
        const { version } = await res.json();
        return version || null;
      } catch {
        return null;
      }
    };

    const check = async () => {
      if (cancelled || toastShown.current) return;
      const v = await fetchVersion();
      if (!v) return;

      // First successful call: remember it, do nothing.
      if (!initialVersion.current) {
        initialVersion.current = v;
        return;
      }

      // Same as initial → no update.
      if (v === initialVersion.current) {
        pendingVersion.current = null;
        return;
      }

      // Different from initial. Debounce: require two consecutive polls
      // returning the SAME new version before notifying the user. This
      // avoids false positives from multi-replica deployments where the
      // load balancer bounces between pods with different versions.
      if (pendingVersion.current !== v) {
        pendingVersion.current = v;
        return;
      }

      toastShown.current = true;
      toast.info("Hay una nueva versión de la aplicación disponible.", {
        duration: Infinity,
        action: {
          label: "Recargar",
          onClick: () => {
            try {
              sessionStorage.clear();
            } catch {
              /* ignore */
            }
            window.location.reload();
          },
        },
      });
    };

    check();
    const id = setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return null;
}
